import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@ngneat/transloco';
import { map } from 'rxjs/operators';
import { USER_QUERIES } from '@/app/platform/platform-core/user/store/types';
import {
  UserDataSyncCategoryStatus,
  UserDataSyncService
} from '@/app/platform/platform-core/user/services/user-data-sync.service';
import { AccountDetails } from '@/app/platform/platform-core/user/models/account-details';

interface UserDataSyncCardVm {
  title: string;
  description: string;
  status: UserDataSyncCategoryStatus;
}

interface AccountDetailRowVm {
  label: string;
  value: string;
}

interface UserDataSyncBadgeVm {
  label: string;
  kind: 'ok' | 'busy' | 'offline' | 'none';
}

/**
 * Inhoud van de "Mijn instellingen"-dialoog: read-only accountgegevens plus de
 * opslag/synchronisatie-status van de automatisch bewaarde app-instellingen en
 * app-status (zie `UserDataSyncService`). Er is bewust geen opslaan-knop: alles
 * wordt automatisch opgeslagen terwijl de gebruiker werkt.
 */
@Component({
    selector: 'user-settings-container',
    templateUrl: './user-settings-container.component.html',
    styleUrls: ['./user-settings-container.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class UserSettingsContainerComponent {
  private readonly userQueries = inject(USER_QUERIES);
  private readonly userDataSync = inject(UserDataSyncService);
  private readonly transloco = inject(TranslocoService);

  /** Actieve app-taal als signal, zodat de vertaalde `computed`s reageren op een taalwissel. */
  private readonly lang = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang()
  });

  private readonly accountDetails = toSignal(
    this.userQueries.authenticatedUser$.pipe(
      map(user => (user ? AccountDetails.fromUser(user) : undefined))
    )
  );

  protected readonly accountRows = computed<AccountDetailRowVm[]>(() => {
    const details = this.accountDetails();
    if (!details) {
      return [];
    }
    const language = this.lang();
    const label = (key: string) =>
      this.transloco.translate<string>(`account.settings.fields.${key}`, undefined, language);
    return [
      { label: label('userName'), value: details.userName },
      { label: label('displayName'), value: details.displayName },
      { label: label('firstName'), value: details.firstName },
      { label: label('lastName'), value: details.lastName },
      { label: label('email'), value: details.email },
      { label: label('createdOn'), value: details.createdOn },
      { label: label('lastModifiedOn'), value: details.lastModifiedOn }
    ];
  });

  protected readonly syncCards = computed<UserDataSyncCardVm[]>(() => {
    const language = this.lang();
    const translate = (key: string) => this.transloco.translate<string>(key, undefined, language);
    return [
      {
        title: translate('account.settings.cards.appSettings.title'),
        description: translate('account.settings.cards.appSettings.description'),
        status: this.userDataSync.settingsStatus()
      },
      {
        title: translate('account.settings.cards.appState.title'),
        description: translate('account.settings.cards.appState.description'),
        status: this.userDataSync.appStateStatus()
      }
    ];
  });

  protected syncBadge(status: UserDataSyncCategoryStatus): UserDataSyncBadgeVm {
    const translate = (key: string) => this.transloco.translate<string>(key, undefined, this.lang());
    if (status.offline) {
      return { label: translate('account.settings.badge.offline'), kind: 'offline' };
    }
    if (status.pending) {
      return { label: translate('account.settings.badge.saving'), kind: 'busy' };
    }
    if (status.serverSavedOn) {
      return { label: translate('account.settings.badge.synced'), kind: 'ok' };
    }
    if (status.localSavedOn) {
      return { label: translate('account.settings.badge.localOnly'), kind: 'none' };
    }
    return { label: translate('account.settings.badge.none'), kind: 'none' };
  }
}
